import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@parrot-software/design-system';
import { Copy } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';

/** Visual treatments available for the CLABE row. */
type ClabeDisplayVariant = 'boxed' | 'flat';

/** Complete class list per variant — never interpolated, so Tailwind's scanner sees them. */
const VARIANT_CLASSES: Record<ClabeDisplayVariant, string> = {
  boxed: 'w-full rounded-lg border border-border p-2.5',
  flat: 'w-full',
};

interface ClabeDisplayProps {
  providerPublicId: string;
  className?: string;
  children?: ReactNode;
  variant?: ClabeDisplayVariant;
}

/**
 * Formats a CLABE for display with spaces: 0121 8000 4567 8901 23
 */
const formatClabe = (value: string): string => {
  const clean = value.replace(/\s/g, '');
  return `${clean.slice(0, 4)} ${clean.slice(4, 8)} ${clean.slice(8, 12)} ${clean.slice(12, 16)} ${clean.slice(16)}`;
};

/**
 * ClabeDisplay - Shows a formatted CLABE with a copy button
 *
 * The copy icon turns primary on hover. After clicking, a "CLABE copiada!"
 * tooltip appears and resets when the user stops hovering. Optional `children`
 * render inside the same box, below the CLABE row (e.g. the bank /
 * recipient pair in the block detail SPEI card).
 */
export const ClabeDisplay = ({ providerPublicId, className, children, variant = 'boxed' }: ClabeDisplayProps) => {
  const { t } = useTranslation('subscriptions');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      const cleanClabe = providerPublicId.replace(/\s/g, '');
      await navigator.clipboard.writeText(cleanClabe);
      setCopied(true);
    } catch {
      /* clipboard API not available */
    }
  };

  const handleMouseLeave = () => {
    if (copied) {
      setCopied(false);
    }
  };

  const isBoxed = variant === 'boxed';

  return (
    <div className={cn(VARIANT_CLASSES[variant], className)}>
      {isBoxed && <p className="text-xs leading-3 text-muted-foreground">{t('paymentMethod.clabeLabel')}</p>}
      {/* No `justify-between`: the copy button sits right after the number (which keeps a
          small right padding) instead of being pushed to the far edge of the box. */}
      <div className={cn('flex items-center', { 'mt-0.5': isBoxed })}>
        <p
          className={cn('pr-1 text-sm font-medium text-foreground break-all', {
            'font-semibold whitespace-nowrap break-normal': isBoxed,
          })}
        >
          {formatClabe(providerPublicId)}
        </p>
        <TooltipProvider delayDuration={0}>
          <Tooltip open={copied}>
            <TooltipTrigger asChild>
              <Button
                aria-label={t('paymentMethod.copyCLABE')}
                className="h-6 w-6 shrink-0 p-0 hover:text-primary"
                onClick={handleCopy}
                onMouseLeave={handleMouseLeave}
                size="icon"
                variant="ghost"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>{t('paymentMethod.clabeCopied')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
};
